import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button, ButtonGroup,
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input,
  Table
} from 'reactstrap';
import { format, parseISO, formatISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { FaCalendarAlt, FaDownload, FaUser, FaFileAlt, FaPrint, FaSearch } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Select from "react-select";
import autoTable from 'jspdf-autotable';
import { supplierActions } from '../../sagas/supplierSlice';
import { purchaseActions } from '../../sagas/purchaseSlice';
import { productActions } from '../../sagas/productSlice';
import { transporteurActions } from '../../sagas/transporteurSlice';

const AchatsStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);
  const [brFilter, setBrFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const unites = useSelector(status => status.products.unites);
  const { transporteurs } = useSelector(state => state.transporteurs);

  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  useEffect(() => {
    dispatch(productActions.findAllUnites());
    dispatch(purchaseActions.clearSupplierReport());
  }, []);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    if (dateString.includes('-')) {
      return parseISO(dateString);
    }
    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  // State
  const [dateRange, setDateRange] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
  });

  // Supplier report state
  const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
  const { supplierReport, supplierReportLoading, supplierReportError } = useSelector(state => state.purchases);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const supplierReportRef = useRef(null);
  const [filters, setFilters] = useState({
    supplierId: null,
    transporteurId: null,
    dateDebut: formatDate(todayDate),
    dateFin: formatDate(tomorrowDate),
    query: ''
  });

  const fournisseurOptions = suppliers?.map(sup => ({
    solde: sup.solde,
    totalDebit: sup.totalDebit,
    totalCredit: sup.totalCredit,
    value: sup.id,
    label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom}` : sup.entreprise,
  })) ?? [];

  const fournisseurOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...fournisseurOptions
  ];

  // Effect for fetching data
  useEffect(() => {
    if (suppliers.length === 0) {
      // Fetch suppliers for supplier report
      const fetchSuppliers = async () => {
        dispatch(supplierActions.search({
          query: ""
        }));
      };

      fetchSuppliers();
    }
    dispatch(transporteurActions.list());

  }, []);

  useEffect(() => {
    generateSupplierReport();
  }, [filters.supplierId, filters.transporteurId, filters.dateDebut, filters.dateFin]);

  // Generate supplier report
  const generateSupplierReport = () => {
    dispatch(purchaseActions.generateSupplierReport({ ...filters }));
  };

  const exportSupplierReportToPDF = () => {
    if (!supplierReport) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const footerHeight = 15;

    // ---- GREEN & WHITE THEME ----
    const primaryGreen = { r: 46, g: 204, b: 113 };
    const altRow = { r: 232, g: 248, b: 245 };
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };

    // ---------------- HEADER ----------------
    const addPageHeader = () => {
      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 0, pageWidth, 30, "F");

      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 25, pageWidth, 5, "F");

      doc.setTextColor(white.r, white.g, white.b);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("RAPPORT ACHAT FOURNISSEUR", pageWidth / 2, 15, { align: "center" });

      const formattedFrom = format(parseDate(supplierReport.period.from), "dd/MM/yyyy", { locale: fr });
      const formattedTo = format(parseDate(supplierReport.period.to), "dd/MM/yyyy", { locale: fr });

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Période: du ${formattedFrom} au ${formattedTo}`, pageWidth / 2, 22, { align: "center" });
    };

    // ---------------- FOOTER ----------------
    const addPageFooter = () => {
      const y = pageHeight - footerHeight;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);

      doc.text(`Document généré le: ${supplierReport.generationDate}`, pageWidth - margin, y + 5, { align: "right" });
      doc.text(company.name, margin, y + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, y + 5, { align: "center" });
    };

    // ---- FIRST PAGE HEADER ----
    addPageHeader();

    // ---- COMPANY INFO ----
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(black.r, black.g, black.b);
    doc.text(company.name, margin, 40);

    doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Adresse: ${company.address}`, margin, 47);
    doc.text(`Tél: ${company.phone1} | Email: ${company.email}`, margin, 52);
    doc.text(`RC: ${company.rc} | IF: ${company.identificationFiscale} | ICE: ${company.ice}`, margin, 57);

    if (supplierReport.supplier) {
      // ---- SUPPLIER BOX ----
      doc.setFillColor(white.r, white.g, white.b);
      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.roundedRect(pageWidth - margin - 80, 35, 80, 30, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("FOURNISSEUR", pageWidth - margin - 75, 42);

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.line(pageWidth - margin - 75, 44, pageWidth - margin - 15, 44);

      doc.setTextColor(black.r, black.g, black.b);
      doc.setFont("helvetica", "normal");
      doc.text(`Nom: ${supplierReport.supplier.name}`, pageWidth - margin - 75, 50);
      doc.text(`Téléphone: ${supplierReport.supplier.phone}`, pageWidth - margin - 75, 55);
      doc.text(`Code: ${supplierReport.supplier.code}`, pageWidth - margin - 75, 60);
    }

    // ---------------- TABLE ----------------
    const headers = ["N° BR", "Date", "Désignation", "Qté", "UM", "PU", "Total"];

    const rows = supplierReport.bonsReception.map(br => [
      br.brNumber,
      format(parseDate(br.date), "dd/MM/yyyy", { locale: fr }),
      br.designation,
      br.quantity.toString(),
      br.unit,
      formatPrice(br.unitPrice, false),
      formatPrice(br.lineTotal, false)
    ]);

    autoTable(doc, {
      startY: 70,
      head: [headers],
      body: rows,
      theme: "grid",

      // ---- FIXED: green header with borders ----
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.3,
        lineColor: [0, 0, 0]
      },

      // ---- alternating rows ----
      alternateRowStyles: {
        fillColor: [altRow.r, altRow.g, altRow.b]
      },

      styles: {
        cellPadding: 3,
        fontSize: 9,
        textColor: [0, 0, 0],
        lineWidth: 0.25,
        lineColor: [150, 150, 150]
      },

      margin: { top: margin, bottom: footerHeight },
      didDrawPage: addPageFooter
    });

    if (supplierReport.supplier) {
      // ---------------- SUMMARY ----------------
      const summaryY = doc.lastAutoTable.finalY + 15;

      // ---- FIXED: rounded green-bordered summary box ----
      const summaryW = 90;
      const summaryH = 40;

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, summaryY, summaryW, summaryH, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFontSize(12);
      doc.text("RÉSUMÉ", margin + 5, summaryY + 7);

      const summary = supplierReport.summary;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(black.r, black.g, black.b);
      doc.setFontSize(9);

      doc.text("Montant Total Consommé:", margin + 5, summaryY + 15);
      doc.text(formatPrice(summary.totalConsome, false), margin + summaryW - 5, summaryY + 15, { align: "right" });

      doc.text("Solde Avant :", margin + 5, summaryY + 22);
      doc.text(formatPrice(summary.soldeAvant, false), margin + summaryW - 5, summaryY + 22, { align: "right" });

      doc.text("Avance :", margin + 5, summaryY + 29);
      doc.text(formatPrice(summary.avance, false), margin + summaryW - 5, summaryY + 29, { align: "right" });

      doc.text("Avoir :", margin + 5, summaryY + 36);
      doc.text(formatPrice(summary.avoir, false), margin + summaryW - 5, summaryY + 36, { align: "right" });

      // ---- Net à payer OUTSIDE the card ----
      const net = parseFloat(summary.netApaye);
      const netY = summaryY + summaryH + 10;

      doc.setFont("helvetica", "bold");
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFontSize(12);
      doc.text("Net À Payer :", margin, netY);

      doc.text(formatPrice(net, false), margin + 60, netY);
    }
    // ---------------- OPEN IN NEW TAB ----------------
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const debouncedSearch = () => {
    dispatch(purchaseActions.generateSupplierReport({ ...filters }));
  }

  const transporteurOptions = transporteurs?.map(transporteur => ({
    value: transporteur.id,
    label: transporteur.code + ' - ' + transporteur.libelle,
  })) ?? [];

  const transporteurOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...transporteurOptions
  ];

  return (
    <Container fluid className="page-content">
      <Card className="shadow-sm mb-4">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaFileAlt className="me-2 text-primary" />
              Détails des achats
            </h5>
            <div className="d-flex gap-2">
              <Button
                color="success"
                className="me-2"
                onClick={generateSupplierReport}
                disabled={supplierReportLoading}
              >
                {supplierReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Génération...
                  </>
                ) : (
                  <>
                    <FaFileAlt className="me-1" /> Générer
                  </>
                )}
              </Button>
              {supplierReport && (
                <>
                  <Button
                    color="secondary"
                    onClick={exportSupplierReportToPDF}
                  >
                    <FaDownload className="me-1" /> Télécharger
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-4">
            <Col lg={3} md={3} sm={6} xs={12}>
              <FormGroup className="mb-0">
                <Label className="mb-1">{t('reglement.search')}</Label>
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <FaSearch />
                  </span>
                  <Input
                    type="text"
                    placeholder="N° BR, Article, Fournisseur..."
                    value={filters.query}
                    onChange={e => {
                      const value = e.target.value.toUpperCase();
                      setFilters(prevFilters => ({
                        ...prevFilters,
                        query: value
                      }));
                    }}
                    className="text-uppercase"
                    style={{ textTransform: 'uppercase' }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        debouncedSearch(e.target.value.toUpperCase());
                      }
                    }}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="supplierSelect" className="form-label">
                  <FaUser className="me-1" /> Fournisseur
                </Label>
                <Select
                  id="supplierSelect"
                  options={fournisseurOptionsWithAll}
                  isLoading={suppliersLoading}
                  value={fournisseurOptionsWithAll.find(option => option.value === filters.supplierId)}
                  onChange={(selectedOption) => {
                    setFilters(f => ({ ...f, supplierId: selectedOption ? selectedOption.value : null }));
                  }}
                  placeholder="Sélectionner..."
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <Label htmlFor="transporteurId">{t('bonLivraison.transporteur')}:</Label>
              <Select
                id="transporteurId"
                options={transporteurOptionsWithAll}
                value={transporteurOptionsWithAll.find(option => option.value === filters.transporteurId)}
                onChange={(selectedOption) => setFilters(f => ({ ...f, transporteurId: selectedOption ? selectedOption.value : null }))}
                placeholder={t('bonLivraison.selectTransporteur')}
                isSearchable
              />

            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="dateDebut"
                  value={filters.dateDebut}
                  onChange={e => setFilters(f => ({ ...f, dateDebut: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="dateFin"
                  value={filters.dateFin}
                  onChange={e => setFilters(f => ({ ...f, dateFin: e.target.value }))}
                />
              </FormGroup>
            </Col>

          </Row>

          <div ref={supplierReportRef}>
            {supplierReport ? (
              <div className="client-report p-4 border rounded-4 bg-white shadow-sm animate__animated animate__fadeIn">
                <div className="report-body mb-4">
                  <div className="table-responsive rounded-3 overflow-hidden">
                   
                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center">N° BR</th>
                          <th className="text-center">Date</th>
                          <th className="text-center">Fournisseur</th>
                          <th className="text-center">Transporteur</th>
                          <th className="text-center">Désignation</th>
                          <th className="text-center">Quantité</th>
                          <th className="text-center">UM</th>
                          <th className="text-end">PU</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierReport.bonsReception.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            <td className="text-center fw-semibold">{note.brNumber}</td>
                            <td className="text-center">{format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr })}</td>
                            <td className="text-center">{note.supplier ? `${note.supplier.name} (${note.supplier.code})` : "Passager"}</td>
                            <td className="text-center">{note.transporteur ? `${note.transporteur.libelle} (${note.transporteur.matricule})` : 'N/A'}</td>
                            <td className="ps-2">{note.designation}</td>
                            <td className="text-center">{note.quantity}</td>
                            <td className="text-center">{unites.find(u => u.code === note.unit)?.libelle}</td>
                            <td className="text-end">{formatPrice(note.unitPrice, false)}</td>
                            <td className="text-end fw-bold">{formatPrice(note.lineTotal, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="5" className="text-end fw-bold">Total:</td>
                          <td className="text-center fw-bold text-primary">
                            {supplierReport.bonsReception.reduce((acc, note) => acc + (Number(note.quantity) || 0), 0)}
                          </td>
                          <td></td>
                          <td></td>
                          <td className="text-end fw-bold text-primary">{formatPrice(supplierReport.bonsReception.reduce((acc, note) => acc + (note.lineTotal || 0), 0), false)}</td>
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                </div>

                {supplierReport && supplierReport.summary && <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avance</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.avance || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Règlements normaux (hors avances)</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                supplierReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(supplierReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(supplierReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">Arrêté le présent document à la somme de:</p>
                        {
                          supplierReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(supplierReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(supplierReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(supplierReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>
                }
                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{supplierReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {supplierReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un fournisseur et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style jsx>{`
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default AchatsStatistiques;
